import { useEffect, useRef, useState } from 'react';

const TOKEN_KEY = 'geodraft_token';
const POLL_MS = 8000;

export interface ConversionProgress {
  stage: string;
  pct: number;
  etaSeconds?: number;
  ageSeconds: number;
}

interface StatusResponse {
  status: string;
  progress?: ConversionProgress | null;
}

export const useConversionProgress = (
  fileIds: string[],
  onFileDone?: () => void,
): Record<string, ConversionProgress> => {
  const [progress, setProgress] = useState<Record<string, ConversionProgress>>({});
  const onDoneRef = useRef(onFileDone);
  onDoneRef.current = onFileDone;
  const notified = useRef<Set<string>>(new Set());
  const idsKey = fileIds.join(',');

  useEffect(() => {
    if (!idsKey) {
      setProgress((prev) => (Object.keys(prev).length ? {} : prev));
      return;
    }
    const ids = idsKey.split(',');
    // A file re-entering processing (re-convert) must be able to notify again.
    for (const id of ids) notified.current.delete(id);
    let cancelled = false;

    const poll = async () => {
      const token = localStorage.getItem(TOKEN_KEY) || '';
      const results = await Promise.all(
        ids.map(async (id): Promise<[string, StatusResponse | null]> => {
          try {
            const res = await fetch(`/api/potree/status/${id}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) return [id, null];
            return [id, (await res.json()) as StatusResponse];
          } catch {
            return [id, null];
          }
        })
      );
      if (cancelled) return;
      for (const [id, data] of results) {
        if (data && data.status !== 'processing' && !notified.current.has(id)) {
          notified.current.add(id);
          onDoneRef.current?.();
        }
      }
      setProgress((prev) => {
        const next: Record<string, ConversionProgress> = {};
        for (const [id, data] of results) {
          if (!data) {
            // transient fetch failure — keep the last known progress
            if (prev[id]) next[id] = prev[id];
          } else if (data.status === 'processing' && data.progress) {
            next[id] = data.progress;
          }
        }
        return next;
      });
    };

    poll();
    const timer = window.setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [idsKey]);

  return progress;
};
